import React from 'react';
import { PlanningHistoryRow } from './types';

interface PlanningHistoryPanelProps {
  rows: PlanningHistoryRow[];
  onOpenManifest: (row: PlanningHistoryRow) => void;
}

const PlanningHistoryPanel: React.FC<PlanningHistoryPanelProps> = ({ rows, onOpenManifest }) => {
  const [search, setSearch] = React.useState('');
  const [severityFilter, setSeverityFilter] = React.useState<'ALL' | 'CRITICAL' | 'WARNING' | 'OK'>('ALL');
  const [sourceFilter, setSourceFilter] = React.useState<'ALL' | 'CHARTER' | 'SCHEDULED'>('ALL');
  const [selectedRow, setSelectedRow] = React.useState<PlanningHistoryRow | null>(null);

  const filteredRows = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter(row => {
      if (sourceFilter !== 'ALL' && row.source !== sourceFilter) return false;
      if (severityFilter !== 'ALL' && row.unplannedSeverity !== severityFilter) return false;
      if (!query) return true;
      const haystack = [
        row.customerName,
        row.bookingId,
        row.pnrCode,
        row.quoteNumber,
        row.tailNumber,
        row.date,
        row.status,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [rows, search, severityFilter, sourceFilter]);

  const unplannedRows = React.useMemo(
    () => filteredRows.filter(row => row.unplannedSeverity !== 'OK'),
    [filteredRows]
  );

  const severityBadgeLabel = (severity: 'CRITICAL' | 'WARNING' | 'OK') => {
    if (severity === 'CRITICAL') return 'BLOCKED';
    if (severity === 'WARNING') return 'ACTION NEEDED';
    return 'READY';
  };

  return (
    <>
      <div className="flex-1 grid grid-cols-1 xl:grid-cols-3 gap-6 overflow-hidden">
        <div className="xl:col-span-1 rounded-[2rem] border border-rose-100 bg-white shadow-sm p-5 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-black uppercase tracking-[0.2em] text-slate-900">Unplanned Queue</h3>
            <span className="text-[10px] font-black uppercase tracking-widest text-rose-600">{unplannedRows.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-3">
            {unplannedRows.length === 0 && (
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-xs font-semibold text-emerald-700">
                All bookings/PNRs are planned and complete.
              </div>
            )}
            {unplannedRows.map(row => (
              <div
                onClick={() => setSelectedRow(row)}
                key={`${row.source}-${row.id}`}
                className="w-full text-left rounded-2xl border border-slate-100 bg-slate-50 p-3 hover:shadow-sm cursor-pointer"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{row.source}</span>
                  <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
                    row.unplannedSeverity === 'CRITICAL' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {severityBadgeLabel(row.unplannedSeverity)}
                  </span>
                </div>
                <p className="mt-1 text-sm font-black text-slate-900 truncate">{row.customerName}</p>
                <p className="text-[11px] text-slate-600 mt-1">Booking: {row.bookingId} · PNR: {row.pnrCode}</p>
                <p className="text-[10px] font-semibold text-slate-500 mt-1">
                  Critical checks: {row.criticalChecksCompleted}/{row.criticalChecksTotal}
                </p>
                <ul className="mt-2 space-y-1">
                  {row.missingItems.map(item => (
                    <li key={`${row.id}-${item}`} className="text-[11px] text-slate-600">• {item}</li>
                  ))}
                </ul>
                {row.source === 'CHARTER' && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenManifest(row);
                    }}
                    className="mt-3 rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-black uppercase tracking-widest text-slate-700"
                  >
                    Open Manifest
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="xl:col-span-2 rounded-[2rem] border border-slate-100 bg-white shadow-sm p-5 overflow-hidden flex flex-col">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h3 className="text-sm font-black uppercase tracking-[0.2em] text-slate-900">Dispatch History</h3>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{filteredRows.length} rows</span>
          </div>
          <div className="mb-3 grid grid-cols-1 md:grid-cols-4 gap-2">
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search booking, PNR, customer, tail..."
              className="md:col-span-2 rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />
            <select
              value={sourceFilter}
              onChange={e => setSourceFilter(e.target.value as any)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="ALL">All Types</option>
              <option value="CHARTER">Charter</option>
              <option value="SCHEDULED">Scheduled</option>
            </select>
            <select
              value={severityFilter}
              onChange={e => setSeverityFilter(e.target.value as any)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="ALL">All States</option>
              <option value="CRITICAL">Critical</option>
              <option value="WARNING">Warning</option>
              <option value="OK">Ready</option>
            </select>
          </div>
          <div className="flex-1 overflow-auto custom-scrollbar">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-white border-b border-slate-100">
                <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">Customer</th>
                  <th className="py-2 pr-3">Booking / PNR</th>
                  <th className="py-2 pr-3">Tail</th>
                  <th className="py-2 pr-3">Pax</th>
                  <th className="py-2 pr-3">Checks</th>
                  <th className="py-2 pr-3">Manifest</th>
                  <th className="py-2 pr-3">State</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(row => (
                  <tr
                    key={`history-${row.source}-${row.id}`}
                    className="border-b border-slate-50 text-[12px] cursor-pointer hover:bg-slate-50/60"
                    onClick={() => setSelectedRow(row)}
                  >
                    <td className="py-2 pr-3 text-slate-600">{row.date || '—'}</td>
                    <td className="py-2 pr-3 font-semibold text-slate-700">{row.source}</td>
                    <td className="py-2 pr-3 font-semibold text-slate-900">{row.customerName}</td>
                    <td className="py-2 pr-3 text-slate-600">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRow(row);
                        }}
                        className="underline underline-offset-2 hover:text-indigo-600"
                      >
                        {row.bookingId} · {row.pnrCode}
                      </button>
                    </td>
                    <td className="py-2 pr-3 text-slate-600">{row.tailNumber}</td>
                    <td className="py-2 pr-3 text-slate-600">{row.pax}</td>
                    <td className="py-2 pr-3 text-slate-600 font-semibold">
                      {row.criticalChecksCompleted}/{row.criticalChecksTotal}
                    </td>
                    <td className="py-2 pr-3 text-slate-600">{row.manifestState}</td>
                    <td className="py-2 pr-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                        row.unplannedSeverity === 'OK'
                          ? 'bg-emerald-100 text-emerald-700'
                          : row.unplannedSeverity === 'WARNING'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-rose-100 text-rose-700'
                      }`}>
                        {severityBadgeLabel(row.unplannedSeverity)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedRow && (
        <div className="fixed inset-0 z-[138] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-6">
          <div className="bg-white rounded-[2rem] w-full max-w-2xl overflow-hidden shadow-2xl">
            <div className="p-5 border-b border-slate-100 flex items-start justify-between">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-indigo-600">Dispatch Detail</p>
                <h3 className="text-lg font-black text-slate-900 mt-1">{selectedRow.customerName}</h3>
                <p className="text-xs text-slate-500 mt-1">
                  {selectedRow.source} · Booking: {selectedRow.bookingId} · PNR: {selectedRow.pnrCode}
                </p>
              </div>
              <button onClick={() => setSelectedRow(null)} className="text-slate-400 hover:text-slate-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div className="rounded-xl border border-slate-100 p-3"><div className="text-slate-400 uppercase tracking-widest text-[9px]">Date</div><div className="font-semibold text-slate-800 mt-1">{selectedRow.date || '—'}</div></div>
                <div className="rounded-xl border border-slate-100 p-3"><div className="text-slate-400 uppercase tracking-widest text-[9px]">Tail</div><div className="font-semibold text-slate-800 mt-1">{selectedRow.tailNumber}</div></div>
                <div className="rounded-xl border border-slate-100 p-3"><div className="text-slate-400 uppercase tracking-widest text-[9px]">Pax</div><div className="font-semibold text-slate-800 mt-1">{selectedRow.pax}</div></div>
                <div className="rounded-xl border border-slate-100 p-3"><div className="text-slate-400 uppercase tracking-widest text-[9px]">Manifest</div><div className="font-semibold text-slate-800 mt-1">{selectedRow.manifestState}</div></div>
              </div>
              <div className="rounded-xl border border-slate-100 p-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Critical Checks</p>
                <p className="mt-1 text-sm font-semibold text-slate-700">
                  {selectedRow.criticalChecksCompleted}/{selectedRow.criticalChecksTotal} complete
                </p>
              </div>
              <div className={`rounded-xl border p-3 ${
                selectedRow.unplannedSeverity === 'CRITICAL'
                  ? 'border-rose-200 bg-rose-50'
                  : selectedRow.unplannedSeverity === 'WARNING'
                  ? 'border-amber-200 bg-amber-50'
                  : 'border-emerald-200 bg-emerald-50'
              }`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Dispatch Status</p>
                  <span className={`px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                    selectedRow.unplannedSeverity === 'CRITICAL'
                      ? 'bg-rose-100 text-rose-700'
                      : selectedRow.unplannedSeverity === 'WARNING'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {selectedRow.unplannedSeverity === 'CRITICAL'
                      ? 'Blocked'
                      : selectedRow.unplannedSeverity === 'WARNING'
                      ? 'Needs Action'
                      : 'Ready'}
                  </span>
                </div>
                <p className="mt-2 text-sm font-semibold text-slate-700">
                  {selectedRow.unplannedSeverity === 'CRITICAL' &&
                    `Dispatch blocked: ${selectedRow.missingItems.length} mandatory item(s) pending.`}
                  {selectedRow.unplannedSeverity === 'WARNING' &&
                    `Dispatch allowed with caution: ${selectedRow.missingItems.length} non-blocking item(s) pending.`}
                  {selectedRow.unplannedSeverity === 'OK' &&
                    'Dispatch ready: all required checks are complete.'}
                </p>
              </div>
              <div className="rounded-xl border border-slate-100 p-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Required Actions</p>
                {selectedRow.missingItems?.length ? (
                  <ul className="mt-2 space-y-1">
                    {selectedRow.missingItems.map((item: string) => (
                      <li key={`${selectedRow.id}-${item}`} className="text-sm text-slate-700">• {item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-sm text-emerald-700 font-semibold">No missing items. Mission is ready.</p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedRow.source === 'CHARTER' && (
                  <button
                    type="button"
                    onClick={() => onOpenManifest(selectedRow)}
                    className="rounded-full bg-indigo-600 text-white px-4 py-2 text-[10px] font-black uppercase tracking-widest"
                  >
                    Open Manifest
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setSelectedRow(null)}
                  className="rounded-full border border-slate-200 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-slate-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PlanningHistoryPanel;
